import PropTypes from "prop-types";
import { createContext, useEffect, useReducer } from "react";
import { useNavigate } from "react-router-dom";

// third-party
import { Chance } from "chance";
import jwtDecode from "jwt-decode";

// reducer - state management
import { LOGIN, LOGOUT } from "@store/actions";
import accountReducer from "@store/accountReducer";

// project imports
import Loader from "@uiComponent/Loader";
import axios from "axios";

//Projects imports
import { API_URL } from "@config";

const chance = new Chance();

// constant
const initialState = {
  isLoggedIn: false,
  isInitialized: false,
  user: null,
};

const verifyToken = (serviceToken) => {
  if (!serviceToken) {
    return false;
  }
  const decoded = jwtDecode(serviceToken);
  /**
   * Property 'exp' does not exist on type '<T = unknown>(token, options?: JwtDecodeOptions | undefined) => T'.
   */
  return decoded.exp > Date.now() / 1000;
};

const setSession = (serviceToken) => {
  if (serviceToken) {
    localStorage.setItem("serviceToken", serviceToken);
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
  } else {
    localStorage.removeItem("serviceToken");
    delete axios.defaults.headers.common.Authorization;
  }
};

// ==============================|| JWT CONTEXT & PROVIDER ||============================== //
const JWTContext = createContext(null);

export const JWTProvider = ({ children }) => {
  const [state, dispatch] = useReducer(accountReducer, initialState);
  const navigate = useNavigate();

  useEffect(() => {
    const init = async () => {
      try {
        const serviceToken = window.localStorage.getItem("serviceToken");
        if (serviceToken && verifyToken(serviceToken)) {
          setSession(serviceToken);
          const response = await axios.get(API_URL + "auth/profile");
          const { auth_token, info } = response.data;

          const { businessData, user, property_id, role } = info;

          setSession(auth_token);


          let is_admin = false;
          let is_staff = false;
          let is_super_admin = false;
          let is_assistant = false;

          if(role === "SUPERADMIN") {
            is_super_admin = true;
          } else if(role === "ADMIN") {
            is_admin = true;
          } else if(role === "STAFF" || role === "Supervisor" || role === "Technician") {
            is_staff = true;
          } else if(role === "ASSISTANT") {
            is_assistant = true;
          }

          dispatch({
            type: LOGIN,
            payload: {
              isLoggedIn: true,
              user: {
                id: user.id,
                email: user.email,
                name: user.display_name || "Usuario nuevo",
                picurl: user.picurl,
                role: 1,
                businessData: businessData,
                sessionToken: auth_token,
                is_admin,
                is_staff,
                is_super_admin,
                is_assistant,
                is_property: role === "PROPERTY",
                propertyID: property_id,
                userRole: role,
              },
            },
          });
        } else {
          dispatch({
            type: LOGOUT,
          });
        }
      } catch (err) {
        console.error(err);
        dispatch({
          type: LOGOUT,
        });
      }
    };

    init();
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(API_URL + "auth/login", {
      email,
      password,
    });
    const { auth_token, info } = response.data;

    const { businessData, user, property_id, role } = info;
    
    let is_admin = false;
    let is_staff = false;
    let is_super_admin = false;
    let is_assistant = false;

    if(role === "SUPERADMIN") {
      is_super_admin = true;
    } else if(role === "ADMIN") {
      is_admin = true;
    } else if(role === "STAFF" || role === "Supervisor" || role === "Technician") {
      is_staff = true;
    } else if(role === "ASSISTANT") {
      is_assistant = true;
    }

    setSession(auth_token);
    dispatch({
      type: LOGIN,
      payload: {
        isLoggedIn: true,
        user: {
          id: user.id,
          email: user.email,
          name: user.display_name || "Usuario nuevo",
          picurl: user.picurl,
          role: 1,
          businessData: businessData,
          sessionToken: auth_token,
          is_admin,
          is_staff,
          is_super_admin,
          is_assistant,
          is_property: role === "PROPERTY",
          propertyID: property_id,
          userRole: role,
        },
      },
    });
  };

  const register = async (email, password, firstName, lastName) => {
    // todo: this flow need to be recode as it not verified
    const id = chance.bb_pin();
    const response = await axios.post(API_URL + "auth/register", {
      id,
      email,
      password,
      firstName,
      lastName,
    });
    let users = response.data;

    if (
      window.localStorage.getItem("users") !== undefined &&
      window.localStorage.getItem("users") !== null
    ) {
      const localUsers = window.localStorage.getItem("users");
      users = [
        ...JSON.parse(localUsers),
        {
          id,
          email,
          password,
          name: `${firstName} ${lastName}`,
        },
      ];
    }

    window.localStorage.setItem("users", JSON.stringify(users));
  };

  const logout = () => {
    setSession(null);
    dispatch({ type: LOGOUT });
    navigate("/login", { replace: true });
  };

  const resetPassword = (email) => console.log(email);

  const updateProfile = () => {};

  if (state.isInitialized !== undefined && !state.isInitialized) {
    return <Loader />;
  }

  return (
    <JWTContext.Provider
      value={{
        ...state,
        login,
        logout,
        register,
        resetPassword,
        updateProfile,
      }}
    >
      {children}
    </JWTContext.Provider>
  );
};

JWTProvider.propTypes = {
  children: PropTypes.node,
};

export default JWTContext;
